import { Button, Typography, Table, Modal, Image, Avatar, notification, Input, Empty, Tag, Tooltip } from "antd"
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, ClockCircleOutlined, UserOutlined, CheckCircleFilled, SearchOutlined, MailOutlined, PhoneOutlined, CalendarOutlined, IdcardOutlined, SafetyCertificateOutlined } from "@ant-design/icons"
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import ajaxService from "../../../services/ajax-service";
import { BASE_URL } from "../../../constants";
import { calculateTableScrollX, calculateModalWidth } from "../../../utils/widthCalculator";

const { Text, Title } = Typography

const UserVerification = () => {

    const [user, setUser] = useState({})
    const [filter, setFilter] = useState('')
    const [searchValue, setSearchValue] = useState('')
    const debounceTimer = useRef(null)
    const isMounted = useRef(true)
    const [showModal, setShowModal] = useState(false)
    const [dataSource, setDataSource] = useState({})
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
    const [showMoreDetailsModal, setShowMoreDetailsModal] = useState(false)
    const [reason, setReason] = useState('')
    const [quantityPaidUser, setQuantityPaidUser] = useState(0)
    const [isVerifying, setIsVerifying] = useState(false)

    const getUserAgeLabel = useCallback((u) => {
        const directAge = u?.age;
        if (directAge !== undefined && directAge !== null && String(directAge).trim() !== '') {
            return String(directAge);
        }

        const dobRaw = u?.dob;
        if (!dobRaw) return 'N/A';

        const dob = new Date(dobRaw);
        if (Number.isNaN(dob.getTime())) return 'N/A';

        const now = new Date();
        let age = now.getFullYear() - dob.getFullYear();
        const monthDiff = now.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
            age -= 1;
        }
        return age >= 0 ? String(age) : 'N/A';
    }, []);

    const getUserDobLabel = useCallback((u) => {
        const dobRaw = u?.dob;
        if (!dobRaw) return 'N/A';

        const dob = new Date(dobRaw);
        if (Number.isNaN(dob.getTime())) return 'N/A';

        return dob.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleClick = (id) => {
        const rows = Array.isArray(dataSource?.data) ? dataSource.data : [];
        const data = rows.find(i => i.id === id);
        if (!data) return;
        data.interest_users = Array.isArray(data?.interest_users) ? data.interest_users.join(', ') : data?.interest_users;
        setUser(data);
        setShowModal(true)
    }

    const pendingRows = useMemo(() => {
        const rows = Array.isArray(dataSource?.data) ? dataSource.data : [];
        return rows.filter(i => String(i?.verification_status || '').toLowerCase() === 'pending');
    }, [dataSource?.data]);

    const totalPending = useMemo(() => pendingRows.length, [pendingRows.length]);

    const renderStatus = (status) => {
        const normalized = String(status || '').toLowerCase();
        if (!normalized) return <Tag>Unknown</Tag>;
        if (normalized === 'pending') return <Tag color="gold">Pending</Tag>;
        if (normalized === 'approved') return <Tag color="green">Approved</Tag>;
        if (normalized === 'rejected') return <Tag color="red">Rejected</Tag>;
        return <Tag color="blue">{String(status)}</Tag>;
    };

    const columns = [
        {
            title: 'User',
            dataIndex: 'name',
            key: 'user',
            width: 320,
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
            sortDirections: ['ascend', 'descend'],
            ellipsis: true,
            render: (name, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <Avatar
                        size={36}
                        icon={<UserOutlined />}
                        src={record?.images && (record.images).length > 0 ? BASE_URL + '/storage/' + record.images[0]?.image : undefined}
                        style={{ border: '1px solid rgba(254, 2, 125, 0.25)', background: 'rgba(254, 2, 125, 0.12)' }}
                    />
                    <div style={{ minWidth: 0 }}>
                        <Text strong style={{ color: 'var(--text-gray-dark)', display: 'block', lineHeight: 1.2 }} ellipsis={{ tooltip: name || 'N/A' }}>
                            {name || 'N/A'}
                        </Text>
                        <Text style={{ color: 'var(--text-gray)', fontSize: 12, display: 'block' }} ellipsis={{ tooltip: record?.email || 'N/A' }}>
                            {record?.email || 'N/A'}
                        </Text>
                    </div>
                </div>
            )
        },
        {
            title: 'Status',
            dataIndex: 'verification_status',
            key: 'verification_status',
            width: 140,
            render: (status) => renderStatus(status),
            filters: [
                { text: 'Pending', value: 'pending' },
                { text: 'Approved', value: 'approved' },
                { text: 'Rejected', value: 'rejected' },
            ],
            onFilter: (value, record) => String(record?.verification_status || '').toLowerCase() === value,
        },
        {
            title: 'Submitted',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 170,
            ellipsis: true,
            render: (created_at) => (
                <Text style={{ color: 'var(--text-gray)', fontSize: 12 }}>
                    {created_at ? new Date(created_at).toLocaleString() : 'N/A'}
                </Text>
            )
        },
        {
            title: 'Actions',
            key: 'address',
            width: 150,
            fixed: 'right',
            render: (text, record) => (
                <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                    <Tooltip title="Review">
                        <Button 
                            type="text"
                            size="small" 
                            style={{ 
                                padding: '4px 8px',
                                fontSize: '12px',
                                color: 'var(--primary-purple)',
                                border: '1px solid var(--primary-purple)',
                                borderRadius: 'var(--radius-md)',
                                transition: 'var(--transition-fast)'
                            }} 
                            icon={<EyeOutlined style={{ fontSize: '12px' }} />}
                            onClick={() => handleClick(record.id)}
                        />
                    </Tooltip>
                    <Tooltip title="Reject">
                        <Button 
                            type="text"
                            size="small" 
                            style={{ 
                                padding: '4px 8px',
                                fontSize: '12px',
                                color: 'var(--error-red)',
                                border: '1px solid var(--error-red)',
                                borderRadius: 'var(--radius-md)',
                                transition: 'var(--transition-fast)'
                            }} 
                            icon={<CloseCircleOutlined style={{ fontSize: '12px' }} />}
                            onClick={() => {
                                setUser(record);
                                setShowMoreDetailsModal(true);
                            }}
                        />
                    </Tooltip>
                </div>
            )
        },
    ];

    const handleChangePage = async (page) => {
        const response = await ajaxService.get(`/verifications?name=${filter}&page=${page}`);
        if (response.success && isMounted.current) {
            response.data = (response.data).filter(i => i.verification_status === 'pending');
            setDataSource(response)
        }

    }

    const handleSearch = async ({ name }) => {
        const searchName = name ? name.trim() : '';
        const response = await ajaxService.get(`/verifications?name=${searchName}`);

        const { success, data } = response;

        if (success && isMounted.current) {
            if (data.length === 0 && searchName) {
                setFilter('')
                notification.open({ message: `${searchName} is not found!`, icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} /> })
            } else {
                setFilter(searchName)
            }

            response.data = (response.data).filter(i => i.verification_status === 'pending');
            setDataSource(response)
        }
    }

    // Debounced search handler
    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current)
        }

        debounceTimer.current = setTimeout(() => {
            handleSearch({ name: searchValue })
        }, 500)

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current)
            }
        }
    }, [searchValue])

    const handleVerify = async (id, status) => {
        if (!id || isVerifying) return;

        setIsVerifying(true);
        try {
            const data = { id: id, status: status };

            const response = await ajaxService.post('/approve-reject-verification', data);
            if (response.success) {
                setShowModal(false);
                initialize();
                notification.open({ message: response.data.verification_status, icon: <CheckCircleOutlined style={{ color: 'var(--primary-purple)' }} /> });
            } else {
                notification.open({ message: response.message, icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} /> });
            }
        } catch (error) {
            console.error('Verify error:', error);
            notification.open({ message: 'Failed to verify user.', icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} /> });
        } finally {
            if (isMounted.current) {
                setIsVerifying(false);
            }
        }
    }

    const handleRequestMore = async (id, reasonText) => {
        if (!reasonText.trim()) {
            notification.open({ message: 'Please provide a reason.', icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} /> })
            return
        }

        const data = { id: id, reject_reason: reasonText, status: 'rejected' }

        const response = await ajaxService.post('/approve-reject-verification', data) // Adjust endpoint as needed
        if (response.success) {
            setShowMoreDetailsModal(false)
            setShowModal(false)
            setReason('')
            initialize()
            notification.open({ message: 'Verification rejected successfully.', icon: <CheckCircleOutlined style={{ color: 'var(--primary-purple)' }} /> })
        } else {
            notification.open({ message: response.message || 'Failed to request more details.', icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} /> })
        }
    }

    const fetchFeatureSetting = async () => {
        try {
            const response = await ajaxService.get('/features-setting?name=verification');
            if (response.success) {
                setQuantityPaidUser(response.data.quantity_paid_user || 0);
            }
        } catch (error) {
            console.error('Failed to fetch feature setting:', error);
        }
    };

    const handleToggleChange = async (checked) => {
        const newValue = checked ? 1 : 0;
        try {
            const updateData = { name: 'verification', value: newValue };
            const response = await ajaxService.post('/features-setting', updateData);
            if (response.success) {
                setQuantityPaidUser(newValue);
                notification.open({
                    message: `Auto verification setting ${checked ? 'enabled' : 'disabled'} successfully.`,
                    icon: <CheckCircleOutlined style={{ color: 'var(--primary-purple)' }} />
                });
            } else {
                throw new Error(response.message || 'Update failed');
            }
        } catch (error) {
            console.error('Failed to update feature setting:', error);
            notification.open({
                message: 'Failed to update verification setting.',
                icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} />
            });
            // Revert on failure
            setQuantityPaidUser(1 - newValue);
        }
    };

    const initialize = useCallback(async () => {
        const response = await ajaxService.get(`/verifications?name=${filter}`);
        if (response.success && isMounted.current) {

            response.data = (response.data).filter(i => String(i?.verification_status || '').toLowerCase() === 'pending');

            setDataSource(response)
        }
    }, [filter])

    useEffect(() => {
        initialize()
        fetchFeatureSetting()
    }, [initialize]);

    // Listen for sidebar collapse state
    useEffect(() => {
        const handleSidebarCollapse = (event) => {
            setSidebarCollapsed(event.detail.collapsed);
        };

        window.addEventListener('sidebarCollapse', handleSidebarCollapse);
        return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
    }, []);

    // Calculate table scroll width using standardized utility
    // Table columns: 300px (Name) + 150px (Actions) = 450px total
    const calculateTableWidth = useCallback(() => {
        return calculateTableScrollX(sidebarCollapsed, 450, 450, 40);
    }, [sidebarCollapsed]);

    const [tableScrollX, setTableScrollX] = useState(() => {
        if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
            return calculateTableScrollX(false, 450, 450, 40);
        }
        return '100%';
    });

    // Recalculate on window resize and sidebar toggle
    useEffect(() => {
        const handleResize = () => {
            setTableScrollX(calculateTableWidth());
        };

        window.addEventListener('resize', handleResize);
        handleResize();
        
        return () => window.removeEventListener('resize', handleResize);
    }, [calculateTableWidth]);

    const renderIdDocument = (imageSrc, label) => (
        <div className="verify-user-modal__id-card">
            <div className="verify-user-modal__id-label">{label}</div>
            {imageSrc ? (
                <Image
                    className="verify-user-modal__id-image"
                    preview={{ mask: <span>Preview</span> }}
                    src={BASE_URL + '/storage/' + imageSrc}
                    alt={label}
                />
            ) : (
                <div className="verify-user-modal__id-placeholder">
                    <IdcardOutlined />
                    <span>No image uploaded</span>
                </div>
            )}
        </div>
    )

    const renderMoreDetailsModal = (
        <Modal
            className="modal-overlay verify-user-modal"
            width={calculateModalWidth('520px')}
            open={showMoreDetailsModal}
            maskClosable={false}
            onCancel={() => {
                setShowMoreDetailsModal(false)
                setReason('')
            }}
            footer={null}
            centered
        >
            <div className="verify-user-modal__shell">
                <div className="verify-user-modal__top">
                    <Title level={4} className="verify-user-modal__title">
                        Reject Verification
                    </Title>
                    <Text className="verify-user-modal__subtitle">
                        Please provide a reason for rejecting this request (max 255 characters).
                    </Text>
                </div>

                <Input.TextArea
                    rows={4}
                    placeholder="Enter reason here..."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={255}
                    showCount
                    className="verify-user-modal__textarea"
                />

                <div className="verify-user-modal__footer">
                    <Button
                        className="btn-secondary"
                        onClick={() => {
                            setShowMoreDetailsModal(false)
                            setReason('')
                        }}
                        style={{ minWidth: '120px', height: '40px' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        className="btn-primary"
                        style={{ minWidth: '120px', height: '40px' }}
                        onClick={() => handleRequestMore(user?.id, reason)}
                        disabled={reason.trim().length === 0}
                    >
                        Submit
                    </Button>
                </div>
            </div>
        </Modal>
    )

    const renderModal = (
        <Modal
            className="modal-overlay verify-user-modal"
            width={calculateModalWidth('640px')}
            open={showModal}
            maskClosable={false}
            closable={!isVerifying}
            onCancel={() => setShowModal(false)}
            footer={null}
            centered
        >
            <div className="verify-user-modal__shell">
                <div className="verify-user-modal__header">
                    <Avatar
                        size={64}
                        className="verify-user-modal__avatar"
                        icon={<UserOutlined />}
                        src={user.images && (user.images).length > 0 ? BASE_URL + '/storage/' + user.images[0]?.image : undefined}
                    />
                    <div className="verify-user-modal__header-main">
                        <div className="verify-user-modal__title-row">
                            <p className="verify-user-modal__name">{user?.name || 'N/A'}</p>
                            <span className="verify-user-modal__badge">Pending Review</span>
                        </div>
                        {!!user?.bio && (
                            <p className="verify-user-modal__bio">{user.bio}</p>
                        )}
                    </div>
                </div>

                <div className="ud-info-grid">
                    <div className="ud-info-tile">
                        <div className="ud-info-label"><MailOutlined /> Email</div>
                        <div className="ud-info-value">{user?.email || 'N/A'}</div>
                    </div>
                    <div className="ud-info-tile">
                        <div className="ud-info-label"><PhoneOutlined /> Phone</div>
                        <div className="ud-info-value">{user?.phone || 'N/A'}</div>
                    </div>
                    <div className="ud-info-tile">
                        <div className="ud-info-label"><CalendarOutlined /> Date of Birth</div>
                        <div className="ud-info-value">{getUserDobLabel(user)}</div>
                    </div>
                    <div className="ud-info-tile">
                        <div className="ud-info-label"><UserOutlined /> Age</div>
                        <div className="ud-info-value">{getUserAgeLabel(user)}</div>
                    </div>
                </div>

                {user?.interests_data?.length > 0 && (
                    <div className="verify-user-modal__interests">
                        <span className="verify-user-modal__section-label">Interests</span>
                        <div className="verify-user-modal__interest-tags">
                            {user.interests_data.map((i, index) => (
                                <Tag key={`interest-${index}`} className="verify-user-modal__interest-tag">
                                    {i.interest_name}
                                </Tag>
                            ))}
                        </div>
                    </div>
                )}

                <div className="verify-user-modal__id-section">
                    <div className="verify-user-modal__section-label">
                        <IdcardOutlined /> Identity Documents
                    </div>
                    <div className="verify-user-modal__id-grid">
                        {renderIdDocument(user?.cnic_front, 'ID Front')}
                        {renderIdDocument(user?.cnic_back, 'ID Back')}
                    </div>
                </div>

                <div className="verify-user-modal__footer">
                    <Button
                        className="btn-secondary"
                        disabled={isVerifying}
                        onClick={() => setShowMoreDetailsModal(true)}
                        style={{ minWidth: '130px', height: '40px' }}
                    >
                        Reject
                    </Button>
                    <Button
                        type="primary"
                        className="btn-primary"
                        loading={isVerifying}
                        disabled={isVerifying}
                        onClick={() => handleVerify(user?.id, 'approved')}
                        style={{ minWidth: '130px', height: '40px' }}
                        icon={!isVerifying ? <SafetyCertificateOutlined /> : undefined}
                    >
                        Verify
                    </Button>
                </div>
            </div>
        </Modal>
    )

    return (
        <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            {/* Header Section */}
            <div className="module-header">
                <div style={{ display: 'flex', gap: 'var(--spacing-lg)', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div>
                        <h1 className="module-title">User Verification</h1>
                        <p className="module-subtitle">Review and approve user verification requests</p>
                    </div>

                   {/* <Card
                        size="small"
                        style={{
                            borderRadius: 'var(--radius-xl)',
                            background: 'var(--dark-bg-card)',
                            border: '1px solid rgba(254, 2, 125, 0.2)',
                            boxShadow: 'var(--shadow-card)',
                            minWidth: 260
                        }}
                        bodyStyle={{ padding: 'var(--spacing-md)' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <div style={{ minWidth: 0 }}>
                                <Text style={{ color: 'var(--text-gray-dark)', fontFamily: 'Poppins-Medium', display: 'block' }}>
                                    Auto Verification
                                </Text>
                                <Text style={{ color: 'var(--text-gray)', fontSize: 12, display: 'block' }}>
                                    {quantityPaidUser === 1 ? 'Enabled' : 'Disabled'}
                                </Text>
                            </div>
                            <Switch checked={quantityPaidUser === 1} onChange={handleToggleChange} />
                        </div>
                    </Card>*/}
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="stats-grid">
                <div className="stats-card">
                    <div className="stats-value">{totalPending}</div>
                    <div className="stats-label">Pending reviews</div>
                    <ClockCircleOutlined style={{ color: 'var(--primary-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
                <div className="stats-card">
                    <div className="stats-value">{dataSource.meta?.total || totalPending}</div>
                    <div className="stats-label">Total requests</div>
                    <UserOutlined style={{ color: 'var(--accent-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
                <div className="stats-card">
                    <div className="stats-value">{quantityPaidUser === 1 ? 'ON' : 'OFF'}</div>
                    <div className="stats-label">Auto verification</div>
                    <CheckCircleFilled style={{ color: 'var(--success-green)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
            </div>

            {/* Filters and Search */}
            <div className="filter-section">
                <div className="filter-row">
                    <div className="filter-item">
                        <Input
                            placeholder="Search users..."
                            prefix={<SearchOutlined style={{ color: 'var(--text-gray-light)' }} />}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onPressEnter={(e) => {
                                setSearchValue(e.target.value);
                                handleSearch({ name: e.target.value });
                            }}
                            className="input-field"
                            allowClear
                        />
                    </div>
                </div>
            </div>

            {/* Verification Table */}
            <div className={`data-table-card ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                <Table
                    columns={columns}
                    dataSource={pendingRows}
                    className="data-table"
                    rowKey='id'
                    pagination={{
                        onChange: (page, size) => handleChangePage(page),
                        current: dataSource.meta?.current_page,
                        pageSize: dataSource.meta?.per_page,
                        total: dataSource.meta?.total,
                        className: "data-table-pagination",
                        showSizeChanger: false,
                        showQuickJumper: false,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} pending verifications`,
                        size: 'small'
                    }}
                    scroll={{ x: tableScrollX }}
                    size="small"
                    bordered={false}
                    locale={{
                        emptyText: (
                            <div style={{ padding: 'var(--spacing-2xl) 0' }}>
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        <div style={{ textAlign: 'center' }}>
                                            <Text style={{ display: 'block', color: 'var(--text-gray-dark)' }}>
                                                No pending verifications
                                            </Text>
                                            <Text style={{ display: 'block', color: 'var(--text-gray)', fontSize: 12 }}>
                                                Try clearing search or check back later.
                                            </Text>
                                        </div>
                                    }
                                />
                            </div>
                        )
                    }}
                />
            </div>

            {renderModal}
            {renderMoreDetailsModal}
        </div>
    )
}

export default UserVerification
